import { FormHandles } from '@unform/core';
import React, { useCallback, useRef } from 'react';
import * as Yup from 'yup';

import { ModalHeader } from '../../components/ModalHeader';
import { InputText } from '../../components/InputText';
import { Icon } from '../../components/Icon';
import { useModal } from '../../hooks/modal';
import { useToast } from '../../hooks/toast';

import { api } from '../../services/api';
import { colors } from '../../styles/tokens';
import { getValidationErrors } from '../../utils/getValidationErrors';

import {
  Badge,
  Cancel,
  Container,
  Content,
  CreditsNote,
  FooterRow,
  FormContainer,
  InputTextArea,
  SecondaryChannels,
  SecondaryRow,
  Submit,
} from './styles';

const SUPPORT_EMAIL = 'suporte@trenzox.com';

interface IFormProps {
  subject: string;
  message: string;
}

const HelpCenter: React.FC = () => {
  const formRef = useRef<FormHandles>(null);
  const { addToast } = useToast();
  const { removeModal } = useModal();

  const handleSubmit = useCallback(
    async (data: IFormProps) => {
      try {
        formRef.current?.setErrors({});

        const schema = Yup.object().shape({
          subject: Yup.string().required('Campo obrigatório'),
          message: Yup.string().required('Campo obrigatório'),
        });

        await schema.validate(data, { abortEarly: false });

        await api.post('support/messages', {
          subject: data.subject,
          message: data.message,
          origin: 'dashboard',
        });

        addToast({
          type: 'success',
          title: 'Mensagem enviada!',
          description: 'Nossa equipe vai te responder em breve.',
        });

        removeModal();
      } catch (err: any) {
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          formRef.current?.setErrors(errors);
          return;
        }

        addToast({
          type: 'error',
          title: 'Ocorreu um erro',
          description: 'Tente novamente mais tarde.',
        });
      }
    },
    [addToast, removeModal],
  );

  return (
    <Container>
      <ModalHeader
        title="Central de ajuda"
        subtitle="Fale direto com a nossa equipe"
      />

      <Content>
        <FormContainer ref={formRef} onSubmit={handleSubmit}>
          <InputText
            label="Assunto"
            name="subject"
            placeholder="Ex.: Dúvida sobre pagamentos"
          />
          <InputTextArea
            label="Mensagem"
            name="message"
            rows={4}
            placeholder="Descreva sua dúvida ou problema"
          />

          <FooterRow>
            <Cancel type="button" onClick={() => removeModal()}>
              Cancelar
            </Cancel>
            <Submit name="submit" value="Enviar mensagem" />
          </FooterRow>
        </FormContainer>

        <SecondaryChannels>
          <SecondaryRow href={`mailto:${SUPPORT_EMAIL}`}>
            <Icon name="mail" size={16} color={colors.onSurfaceVariant} />
            <span>Prefere e-mail? {SUPPORT_EMAIL}</span>
          </SecondaryRow>

          <SecondaryRow as="div" disabled>
            <Icon name="chat" size={16} color={colors.onSurfaceVariant} />
            <span>WhatsApp</span>
            <Badge>Em breve</Badge>
          </SecondaryRow>
        </SecondaryChannels>

        <CreditsNote>
          Exercise data by{' '}
          <a href="https://repdb.co" target="_blank" rel="noreferrer">
            RepDB (repdb.co)
          </a>
        </CreditsNote>
      </Content>
    </Container>
  );
};

export { HelpCenter };
